import React, { useEffect, useState } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import EmptyState from '../components/ui/EmptyState';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useToast } from '../contexts/ToastContext';
import {
  FEATURE_CATALOG,
  LIMIT_CATALOG,
  UNLIMITED_LIMIT,
  buildDefaultFeatures,
  buildDefaultLimits,
  buildFeaturesFromPlan,
  buildLimitsFromPlan,
  countEnabledFeatures,
} from '../constants/billingCatalog';
import {
  MAX_WEBSITE_BULLETS,
  bulletsToText,
  formatPlanPrice,
  priceWithGst,
  textToBullets,
  toMajorInput,
  toMinorUnits,
  websiteBulletsError,
  withGstHint,
  yearlyAtDiscount,
  yearlyPriceHint,
} from '../utils/planForm';

const EMPTY_FORM = {
  code: '',
  name: '',
  description: '',
  status: 'draft',
  sortOrder: 100,
  isPublic: true,
  currency: 'INR',
  priceMonthlyMajor: '',
  priceYearlyMajor: '',
  featured: false,
  websiteBulletsText: '',
  features: buildDefaultFeatures(),
  limits: buildDefaultLimits(),
};

function planHasTemplates(plan) {
  return (plan?.features || []).some(
    (feature) => feature.featureKey === 'templates' && feature.enabled,
  );
}

function Plans() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchPlans = async (page = 1) => {
    setLoading(true);
    try {
      const body = {
        page,
        limit: meta.limit || 50,
      };
      if (filters.status) body.status = filters.status;
      if (filters.search.trim()) body.search = filters.search.trim();

      const response = await api.post('/admin/plans/list', body);
      setItems(response.data?.data || []);
      setMeta(response.data?.meta || { page, limit: 50, total: 0, totalPages: 1 });
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error(error.response?.data?.message || 'Failed to load plans');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      features: buildDefaultFeatures(),
      limits: buildDefaultLimits(),
    });
    setShowModal(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      code: plan.code || '',
      name: plan.name || '',
      description: plan.description || '',
      status: plan.status || 'draft',
      sortOrder: plan.sortOrder ?? 100,
      isPublic: plan.isPublic !== false,
      currency: plan.currency || 'INR',
      priceMonthlyMajor: toMajorInput(plan.priceMonthly),
      priceYearlyMajor: toMajorInput(plan.priceYearly),
      featured: Boolean(plan.featured),
      websiteBulletsText: bulletsToText(plan.websiteBullets),
      features: buildFeaturesFromPlan(plan),
      limits: buildLimitsFromPlan(plan),
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'sortOrder' ? Number(value) : value,
    }));
  };

  const handleFeatureToggle = (featureKey, enabled) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.map((feature) => (
        feature.featureKey === featureKey
          ? { ...feature, enabled: Boolean(enabled) }
          : feature
      )),
    }));
  };

  const handleLimitChange = (limitKey, value) => {
    const limitValue = value === '' ? 0 : Number(value);
    setForm((prev) => ({
      ...prev,
      limits: prev.limits.map((limit) => (
        limit.limitKey === limitKey
          ? { ...limit, limitValue: Number.isFinite(limitValue) ? limitValue : 0 }
          : limit
      )),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const bulletsError = websiteBulletsError(form.websiteBulletsText);
    if (bulletsError) {
      toast.error(bulletsError);
      return;
    }
    setSaving(true);
    try {
      let priceMonthly;
      let priceYearly;
      try {
        priceMonthly = toMinorUnits(form.priceMonthlyMajor);
        priceYearly = toMinorUnits(form.priceYearlyMajor);
      } catch (parseError) {
        toast.error(parseError.message);
        setSaving(false);
        return;
      }

      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        status: form.status,
        sortOrder: Number(form.sortOrder) || 0,
        isPublic: Boolean(form.isPublic),
        currency: (form.currency || 'INR').toUpperCase(),
        priceMonthly,
        priceYearly,
        featured: Boolean(form.featured),
        websiteBullets: textToBullets(form.websiteBulletsText),
        features: form.features,
        limits: form.limits,
      };

      if (editing) {
        await api.post('/admin/plans/update', { id: editing.id, ...payload });
        toast.success('Plan updated');
      } else {
        await api.post('/admin/plans/create', payload);
        toast.success('Plan created');
      }
      setShowModal(false);
      await fetchPlans(meta.page || 1);
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error(error.response?.data?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchPlans(1);
  };

  if (loading && items.length === 0) {
    return <LoadingScreen message="Loading plans..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Plans"
        description="Manage subscription plans, feature access (including Templates), limits, and checkout prices."
        action={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New plan
          </button>
        }
      />

      <form className="card card__body" onSubmit={handleFilterSubmit} style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', alignItems: 'end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="planStatus">Status</label>
            <select
              id="planStatus"
              className="form-select"
              value={filters.status}
              onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="deprecated">Deprecated</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" htmlFor="planSearch">Search</label>
            <input
              id="planSearch"
              className="form-input"
              value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              placeholder="Name or code"
            />
          </div>
          <button type="submit" className="btn btn--secondary">Apply</button>
        </div>
      </form>

      {items.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No plans found"
            description="Create a paid plan and set monthly/yearly prices so tenants can checkout."
            action={
              <button type="button" className="btn btn--primary" onClick={openCreate}>
                New plan
              </button>
            }
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Monthly (+ GST)</th>
                  <th>Yearly (+ GST)</th>
                  <th>Website</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((plan) => (
                  <tr key={plan.id}>
                    <td data-label="Plan">
                      <strong>{plan.name}</strong>
                      {plan.featured && <span className="badge badge--info" style={{ marginLeft: '0.5rem' }}>Most popular</span>}
                      <div className="form-hint">
                        <span className="cell-mono">{plan.code}</span>
                        {' · '}{countEnabledFeatures(plan.features)} features
                        {planHasTemplates(plan) ? ' · Templates' : ''}
                      </div>
                    </td>
                    <td data-label="Status">
                      <StatusBadge status={plan.status} />
                    </td>
                    <td data-label="Monthly">{formatPlanPrice(plan.priceMonthly)}</td>
                    <td data-label="Yearly">
                      {formatPlanPrice(plan.priceYearly)}
                      {plan.priceYearly ? (
                        <div className="form-hint">
                          {yearlyPriceHint(toMajorInput(plan.priceMonthly), toMajorInput(plan.priceYearly))}
                        </div>
                      ) : null}
                    </td>
                    <td data-label="Website">
                      {plan.isPublic ? 'Shown' : 'Hidden'}
                      <div className="form-hint">Order {plan.sortOrder}</div>
                    </td>
                    <td data-label="Actions" className="cell-actions">
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        onClick={() => openEdit(plan)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <Modal
          title={editing ? `Edit ${editing.name}` : 'New plan'}
          subtitle="Configure module access, quotas, and checkout prices. Use -1 for unlimited limits."
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="code">Code</label>
                <input
                  id="code"
                  name="code"
                  className="form-input"
                  value={form.code}
                  onChange={handleFormChange}
                  required
                  disabled={Boolean(editing)}
                  placeholder="pro"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="name">Name</label>
                <input
                  id="name"
                  name="name"
                  className="form-input"
                  value={form.name}
                  onChange={handleFormChange}
                  required
                  placeholder="Pro"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="description">Description</label>
              <input
                id="description"
                name="description"
                className="form-input"
                value={form.description}
                onChange={handleFormChange}
                placeholder="Optional"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  className="form-select"
                  value={form.status}
                  onChange={handleFormChange}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="deprecated">Deprecated</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="currency">Currency</label>
                <input
                  id="currency"
                  name="currency"
                  className="form-input"
                  value={form.currency}
                  onChange={handleFormChange}
                  maxLength={3}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="sortOrder">Sort order</label>
                <input
                  id="sortOrder"
                  name="sortOrder"
                  type="number"
                  className="form-input"
                  value={form.sortOrder}
                  onChange={handleFormChange}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="priceMonthlyMajor">Monthly price (₹, before GST)</label>
                <input
                  id="priceMonthlyMajor"
                  name="priceMonthlyMajor"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-input"
                  value={form.priceMonthlyMajor}
                  onChange={handleFormChange}
                  placeholder="699"
                />
                <p className="form-hint">
                  {withGstHint(form.priceMonthlyMajor) ? `${withGstHint(form.priceMonthlyMajor)}. ` : 'GST is added at checkout. '}
                  Leave blank to hide monthly billing.
                </p>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="priceYearlyMajor">Yearly price (₹, before GST)</label>
                <input
                  id="priceYearlyMajor"
                  name="priceYearlyMajor"
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-input"
                  value={form.priceYearlyMajor}
                  onChange={handleFormChange}
                  placeholder="6708"
                />
                <p className="form-hint">
                  {yearlyPriceHint(form.priceMonthlyMajor, form.priceYearlyMajor) || 'Total for 12 months, paid once.'}
                  {priceWithGst(form.priceYearlyMajor) ? ` · ${priceWithGst(form.priceYearlyMajor)} with GST` : ''}
                  {Number(form.priceMonthlyMajor) > 0 && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        style={{ padding: '0 0.25rem' }}
                        onClick={() => setForm((prev) => ({
                          ...prev,
                          priceYearlyMajor: yearlyAtDiscount(prev.priceMonthlyMajor, 20),
                        }))}
                      >
                        Set 20% off
                      </button>
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  name="isPublic"
                  type="checkbox"
                  checked={form.isPublic}
                  onChange={handleFormChange}
                />
                Public (shown on the website and to customers at checkout)
              </label>
            </div>

            <div className="form-group">
              <div className="form-label">Website</div>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 400 }}>
                <input
                  name="featured"
                  type="checkbox"
                  checked={form.featured}
                  onChange={handleFormChange}
                />
                Show as &ldquo;Most popular&rdquo; on the pricing page (only one plan can have this)
              </label>
              <label className="form-label" htmlFor="websiteBulletsText" style={{ marginTop: '0.75rem' }}>
                Bullet points on the pricing card
              </label>
              <textarea
                id="websiteBulletsText"
                name="websiteBulletsText"
                className="form-input"
                rows={4}
                value={form.websiteBulletsText}
                onChange={handleFormChange}
                placeholder={'One per line, e.g.\nUnlimited message templates\nShared team inbox'}
              />
              <p className="form-hint">
                {websiteBulletsError(form.websiteBulletsText)
                  || `Up to ${MAX_WEBSITE_BULLETS} lines. A first line ending in “:” (e.g. “Everything in Starter, plus:”) shows as a heading. Leave empty to build them from the limits and features below.`}
              </p>
            </div>

            <div className="form-group">
              <div className="form-label">Features</div>
              <p className="form-hint" style={{ marginTop: 0 }}>
                Enable module access for this plan. Turn on <strong>Templates</strong> to allow template sync and management.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                {FEATURE_CATALOG.map((feature) => {
                  const row = form.features.find((item) => item.featureKey === feature.key);
                  return (
                    <label
                      key={feature.key}
                      className="form-label"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(row?.enabled)}
                        onChange={(e) => handleFeatureToggle(feature.key, e.target.checked)}
                      />
                      {feature.label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="form-group">
              <div className="form-label">Limits</div>
              <p className="form-hint" style={{ marginTop: 0 }}>
                Numeric quotas per organization. Use {UNLIMITED_LIMIT} for unlimited.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                {LIMIT_CATALOG.map((limit) => {
                  const row = form.limits.find((item) => item.limitKey === limit.key);
                  return (
                    <label key={limit.key} className="form-group" style={{ margin: 0 }}>
                      <span className="form-label">{limit.label}</span>
                      <input
                        type="number"
                        className="form-input"
                        value={row?.limitValue ?? 0}
                        onChange={(e) => handleLimitChange(limit.key, e.target.value)}
                      />
                      {Number(row?.limitValue) === UNLIMITED_LIMIT && <span className="form-hint">Unlimited</span>}
                      {Number(row?.limitValue) === 0 && <span className="form-hint">Not included</span>}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="btn-group modal__sticky-actions">
              <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create plan'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default Plans;
